/**
 * PROFILE MANAGEMENT HOOK
 * =======================
 *
 * User profile state (the `profiles` row) plus avatar uploads via the `api`
 * function.
 *
 * The profile lives in one Context rather than per-caller hook state, for the
 * same reason auth does. Every `useProfile()` caller otherwise fetches its own
 * copy on mount, and — worse — the copies disagree after a write: saving on
 * the Settings page updated only Settings' copy, so `useFormatDate` went on
 * formatting every date in the app with the old dateFormat until a full page
 * reload. One provider means a save is visible everywhere immediately.
 */

import {
  useState,
  useEffect,
  useCallback,
  useRef,
  createContext,
  useContext,
  type ReactNode,
} from 'react';
import { client, invokeFunction } from '@/integrations/neon/client';
import { useAuth } from './useAuth';

type Json = string | number | boolean | null | { [key: string]: Json | undefined } | Json[]

interface Profile {
  id: string;
  user_id: string;
  username?: string | null;
  full_name?: string | null;
  avatar_url?: string | null;
  bio?: string | null;
  timezone: string | null;
  notification_preferences: Json;
  created_at: string;
  updated_at: string;
}


/**
 * Always an Error rather than a bare string: every caller reports failures as
 * `error.message`, and the bare strings these methods used to return produced
 * "Failed to update profile: undefined".
 */
interface ProfileMethods {
  updateProfile: (updates: Partial<Profile>) => Promise<{ error: Error | null }>;
  uploadAvatar: (file: File) => Promise<{ error: Error | null; url?: string }>;
  deleteAvatar: () => Promise<{ error: Error | null }>;
}

type ProfileState = {
  profile: Profile | null;
  loading: boolean;
  error: string | null;
} & ProfileMethods;

function useProfileState(): ProfileState {
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Keyed on the id alone, never the user object: the auth adapter emits a new
  // user object on every event, and depending on the object re-ran this fetch
  // on a loop, holding `loading` true and leaving save buttons disabled. The
  // profile-creation branch below needs other user fields, which it reads off a
  // ref so they never become fetch triggers.
  const userId = user?.id ?? null;
  const userRef = useRef(user);
  userRef.current = user;

  const newProfileRow = useCallback((id: string) => {
    const currentUser = userRef.current;
    return {
      user_id: id,
      // The Neon adapter maps Better Auth's `name` to
      // user_metadata.displayName, not full_name.
      full_name: currentUser?.user_metadata?.displayName || '',
      username: currentUser?.email?.split('@')[0] || '',
      timezone: 'UTC',
      notification_preferences: {
        reviewDue: true,
        monthlyReport: true,
        generalUpdates: true,
      },
    };
  }, []);

  /**
   * Every read and write here takes the rows back as an array and checks how
   * many came out. PostgREST answers a statement that matched no row with 200
   * and an empty body rather than an error, so `error === null` alone says
   * nothing about whether anything was stored — that is what let a save that
   * wrote nothing report success.
   */
  const fetchProfile = useCallback(async () => {
    if (!userId) {
      setProfile(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await client
        .from('profiles')
        .select('*')
        .eq('user_id', userId);

      if (error) {
        setError(error.message);
        return;
      }

      if (data && data.length > 0) {
        setProfile(data[0] as Profile);
        setError(null);
        return;
      }

      const { data: created, error: createError } = await client
        .from('profiles')
        .insert([newProfileRow(userId)])
        .select();

      if (createError) {
        setError(createError.message);
        return;
      }

      if (!created || created.length === 0) {
        setError(
          'The database accepted your profile but stored no row, so settings cannot be saved. This usually means a row-level security policy rejected the write.',
        );
        return;
      }

      setProfile(created[0] as Profile);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  }, [userId, newProfileRow]);

  // Update profile
  const updateProfile = useCallback(async (updates: Partial<Profile>) => {
    if (!userId) {
      return { error: new Error('Not signed in') };
    }

    try {
      const { data, error } = await client
        .from('profiles')
        .update(updates)
        .eq('user_id', userId)
        .select();

      if (error) {
        return { error: new Error(error.message) };
      }

      if (data && data.length > 0) {
        setProfile(data[0] as Profile);
        return { error: null };
      }

      // 200 with no rows back: no profile row matched, so the update changed
      // nothing. Create the row carrying the same changes rather than reporting
      // a save that stored nothing.
      const { data: created, error: createError } = await client
        .from('profiles')
        .insert([{ ...newProfileRow(userId), ...updates }])
        .select();

      if (createError) {
        return { error: new Error(createError.message) };
      }

      if (!created || created.length === 0) {
        return {
          error: new Error(
            'The database accepted the write but stored no row. This usually means a row-level security policy rejected it.',
          ),
        };
      }

      setProfile(created[0] as Profile);
      return { error: null };
    } catch (err) {
      return { error: err instanceof Error ? err : new Error('An unexpected error occurred') };
    }
  }, [userId, newProfileRow]);

  // Upload avatar
  const uploadAvatar = useCallback(async (file: File) => {
    if (!user) {
      return { error: new Error('No user found') };
    }

    try {
      // Upload to the avatars bucket via the `api` Neon Function
      const formData = new FormData();
      formData.append('file', file);

      const { data, error: uploadError } = await invokeFunction<{ url: string }>('/avatar', {
        body: formData,
      });

      if (uploadError || !data?.url) {
        return { error: uploadError ?? new Error('Avatar upload failed') };
      }

      const avatarUrl = data.url;

      // Update profile with new avatar URL
      const { error: updateError } = await updateProfile({ avatar_url: avatarUrl });

      if (updateError) {
        return { error: updateError };
      }

      return { error: null, url: avatarUrl };
    } catch (err) {
      return { error: err instanceof Error ? err : new Error('An unexpected error occurred') };
    }
  }, [user, updateProfile]);

  // Delete avatar
  const deleteAvatar = useCallback(async () => {
    if (!user || !profile?.avatar_url) {
      return { error: new Error('No user or avatar found') };
    }

    try {
      // Delete the user's avatar objects from the bucket
      const { error: deleteError } = await invokeFunction('/avatar/delete');

      if (deleteError) {
        return { error: deleteError };
      }

      // Update profile to remove avatar URL
      const { error: updateError } = await updateProfile({ avatar_url: null });

      if (updateError) {
        return { error: updateError };
      }

      return { error: null };
    } catch (err) {
      return { error: err instanceof Error ? err : new Error('An unexpected error occurred') };
    }
  }, [user, profile, updateProfile]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  return {
    profile,
    loading,
    error,
    updateProfile,
    uploadAvatar,
    deleteAvatar,
  };
}

// One provider per app, inside AuthProvider: the fetch below keys off the
// signed-in user.
const ProfileContext = createContext<ProfileState | null>(null);

export function ProfileProvider({ children }: { children: ReactNode }) {
  const value = useProfileState();
  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- the hook belongs with its provider; only fast refresh is affected.
export function useProfile(): ProfileState {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
}